// Segment + measure config for the Production view, over core's build-plan cube.
// Every measure is parked at (facility, production item, build period), so each
// is valid at every segmentation and this file needs no validity matrix.

import type { FilterEntry } from "../../components/ViewControls";
import { BASE_UNIT_SPLIT_COLUMN, BUILD_PLAN_MEASURE, formatMeasureCell, MODULE_CODE, spansProductLines } from "./constants";

export { MODULE_CODE };

export type BuildUnits = "eaches" | "cases" | "base_units";

/** Suffixes the cube spells each unit twin of a quantity measure with. */
export const BUILD_UNIT_SUFFIX: Record<BuildUnits, string> = {
  cases: "_cases",
  base_units: "_base_units",
  eaches: "",
};

/** Kept for the readers that name the cases twin directly. */
export const CASES_SUFFIX = BUILD_UNIT_SUFFIX.cases;

// Cases leads because it is the unit a line is scheduled in; it is a no-op
// wherever the carton factor is 1.
export const BUILD_UNIT_OPTIONS: { value: BuildUnits; label: string }[] = [
  { value: "cases", label: "Cases" },
  // An each is one saleable unit of the SKU — a 30-count box, a 4-pack of cans,
  // a single can — so no pack word is true of all of them.
  { value: "eaches", label: "Eaches" },
  // The base unit the engine itself converts in: a can on one product line, a
  // stick on the other. A Build Plan view names its own line's (baseUnitLabel);
  // without one, both words, as the Transfers tab names it.
  { value: "base_units", label: "Cans / Sticks" },
];

export const BUILD_UNIT_LABEL: Record<BuildUnits, string> = {
  cases: "cases",
  eaches: "eaches",
  base_units: "cans / sticks",
};

/** The cube column for a measure at the chosen units. */
export function buildMeasureKey(measure: string, units: BuildUnits): string {
  return `${measure}${BUILD_UNIT_SUFFIX[units]}`;
}

/** The unit whose total is only honest within ONE product line. The column that
 *  tells them apart, and the test for spanning two, are shared with the
 *  Transfers tab — same unit, same rule. */
export const BUILD_BASE_UNIT_MODE: BuildUnits = "base_units";
export { BASE_UNIT_SPLIT_COLUMN, spansProductLines };

export interface BuildSegmentOption {
  value: string;
  label: string;
  description?: string;
}

// "Manufacturing" qualifies both the facility and the SKU: the co-man that
// builds, and what it fills — not the 3PL the Balance Walk calls "Warehouse",
// nor the finished good. "Manufacturing Facility" also matches the Transfers tab.
export const BUILD_SEGMENT_OPTIONS: BuildSegmentOption[] = [
  {
    value: "facility",
    label: "Manufacturing Facility",
    description: "The co-man that builds it.",
  },
  { value: "class", label: "Product Class" },
  {
    value: "pack_type",
    label: "Pack Type",
    description:
      "Container size and pack count together, 12oz and 16oz apart. A " +
      "readability grouping, not the engine's per-flavour batch segment.",
  },
  { value: "container_size", label: "Container Size" },
  { value: "pack_count", label: "Pack Count" },
  { value: "flavor", label: "Flavor" },
  {
    value: "production_sku",
    label: "Manufacturing SKU",
    description: "What the co-man builds, not the finished good a repack ships.",
  },
  {
    value: "finished_good_sku",
    label: "Finished Goods SKU",
    description:
      "The sellable finished good the manufacturing SKU is built into. On the " +
      "recommended plan only.",
  },
  {
    value: "destination_type",
    label: "Destination Type",
    description:
      "Repack where the manufacturing SKU and finished good differ (it will be " +
      "repacked), otherwise 3PL.",
  },
];

export const BUILD_SEGMENT_LABEL: Record<string, string> = Object.fromEntries(
  BUILD_SEGMENT_OPTIONS.map((o) => [o.value, o.label])
);

/** A per-row column: where the build ships — `3PL` (as built) or `Repack` (built
 *  as one item, repacked into another). A normal segment/column, NOT the tab
 *  split. Core computes it per plan arm (co-man off the two keys, finished-goods
 *  off the recipe). */
export const DESTINATION_TYPE_COLUMN = "destination_type";

/** The cube column the In-Line / Repack tabs actually split on: the build SITE's
 *  twin function. `fill` -> In-Line, `kitting` (a repack site) -> Repack. Core
 *  reads the code off the twin, the same on both plan arms — see BuildPlanCube's
 *  `lockedFunction`. */
export const FACILITY_FUNCTION_COLUMN = "facility_function";
export type BuildFunction = "fill" | "kitting";

/** What the page opens on — Neal's one-view column set: where it's built, the
 *  pack, what to build, what it's for, and whether it repacks. Nothing to
 *  configure. */
export const DEFAULT_BUILD_SEGMENTS = [
  "facility",
  "pack_type",
  "production_sku",
  "finished_good_sku",
  DESTINATION_TYPE_COLUMN,
];

/** The grain a lock is published at, which is what the grid is forced to while
 *  it is being edited: a row that mixes two items cannot commit either. */
export const LOCK_GRAIN_SEGMENTS = ["facility", "production_sku"];

/** A read-only column core carries on the repack arm: the manufacturing item(s)
 *  a finished good is built from — a variety pack lists all of them. Neal's ask
 *  to see the manufacturer SKU beside the finished good while editing. */
export const MANUFACTURING_READOUT_COL = "manufacturing_skus";

/** What the edit grid GROUPS by: the lock grain, plus the read-only
 *  manufacturing readout beside the finished good she edits. The readout is 1:1
 *  with the finished good, so it adds a column, never a row. */
export const EDIT_SEGMENTS = ["facility", MANUFACTURING_READOUT_COL, "production_sku"];

/** The grain a build column is bucketed at. Core does the bucketing. */
export type BuildPeriod = "day" | "week" | "month" | "quarter" | "year";

export const BUILD_PERIOD_LABEL: Record<BuildPeriod, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

/** What one mount of the Production pivot offers: the fields it can be grouped
 *  by, the ones it opens on, and its period grains (the first is the default). */
export interface BuildPlanView {
  segments: readonly string[];
  defaultSegments: readonly string[];
  periods: readonly BuildPeriod[];
  /** REQUIRED: the upload group of the committed plan this mount speaks for.
   *  Lock status and the firm zone are per upload group, so the view is read
   *  for exactly one — fixed here per page rather than offered as a control.
   *  The code is the catalog's (core.time_series_upload_group). */
  uploadGroup: string;
  /** The stages its Stage control offers. Absent: every stage. */
  stages?: readonly [BuildStage, ...BuildStage[]];
  /** Its base unit's name, one product line's: Cans or Sticks (a plural in -s; the singular drops it). */
  baseUnitLabel?: string;
}

/** The Sparkling page's Build Plan tab: co-man and SKU, with the pack and
 *  flavor rollups, by the period it is built in, for the `sparkling` upload group.
 *
 *  Drink Mix has its OWN view below, the same but for `uploadGroup`. Mounting
 *  this one there would show Drink Mix rows under Sparkling's firm zone and
 *  publish them into Sparkling's plan. */
export const BUILD_PLAN_VIEW: BuildPlanView = {
  // Co-man and SKU are the planning level; the rest are what a plan is rolled
  // up by. The finished good and destination are left to the Production pivot:
  // they describe where a build goes, not what this tab plans.
  segments: ["facility", "pack_type", "container_size", "pack_count", "flavor", "production_sku"],
  defaultSegments: ["facility", "production_sku"],
  periods: ["week", "day", "month", "quarter"],
  uploadGroup: "sparkling",
  baseUnitLabel: "Cans",
};

/** The Drink Mix page's Build Plan tab. Drink Mix's upload group is the
 *  catalog's `all` — the single-upload group, displayed "Drink Mix". It never
 *  repacks, so First Pack (the stage that locks) is its only stage. */
export const DRINK_MIX_BUILD_PLAN_VIEW: BuildPlanView = {
  ...BUILD_PLAN_VIEW,
  uploadGroup: "all",
  stages: ["first_pack"],
  baseUnitLabel: "Sticks",
};

/** The production stage the Build Plan tab reads: the fill sites' builds, the
 *  repack site's, or both with the stage leading the rows. */
export type BuildStage = "first_pack" | "repack" | "all" | "finished_goods";

export const DEFAULT_BUILD_STAGE: BuildStage = "first_pack";

export const BUILD_STAGE_OPTIONS: { value: BuildStage; label: string; disabled?: boolean }[] = [
  { value: "first_pack", label: "First Pack" },
  { value: "repack", label: "Repack" },
  { value: "all", label: "All" },
  { value: "finished_goods", label: "Finished Goods" },
];

/** The stage options a mount offers: its view's, or every stage. */
export const buildStageOptionsFor = (view?: BuildPlanView): typeof BUILD_STAGE_OPTIONS => {
  const offered = view?.stages;
  return offered ? BUILD_STAGE_OPTIONS.filter((o) => offered.includes(o.value)) : BUILD_STAGE_OPTIONS;
};

/** The build-site function a stage narrows the read to; absent reads both. */
export const BUILD_STAGE_FUNCTION: Partial<Record<BuildStage, BuildFunction>> = {
  first_pack: "fill",
  repack: "kitting",
};

/** FINISHED GOODS: where each sellable item is MADE — First Pack for what ships
 *  as filled, Repack for what is repacked — so the fill rows bound for a repack
 *  site drop out and the repack site's own rows stand in for them. That is the
 *  rows core marks `3PL` across both sites. Read-only: a lock is one site
 *  function's week, and this stage spans two. */
export const FINISHED_GOODS_DESTINATION = "3PL";

/** A site function as the Stage column prints it. */
export const BUILD_STAGE_VALUE_LABEL: Record<string, string> = {
  fill: "First Pack",
  kitting: "Repack",
};

/** Which of the run's two plans the recommended half reads. Core owns the
 *  vocabulary; these are the labels from the 2026-09-21 call — "Manufacturing"
 *  is what a co-man fills first, "With repack" the finished goods that come out
 *  the other side — so a planner reading both surfaces meets one pair of
 *  names. */
export type BuildPlanType = "coman" | "repack";

export const BUILD_PLAN_TYPE_OPTIONS: { value: BuildPlanType; label: string }[] = [
  { value: "coman", label: "Manufacturing" },
  { value: "repack", label: "With repack" },
];

export const BUILD_PLAN_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  BUILD_PLAN_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

/** What the page opens on. The read view stays the co-man's, so it and the
 *  Build Plan (Engine) report state one recommendation cell for cell. */
export const DEFAULT_BUILD_PLAN_TYPE: BuildPlanType = "coman";

/** What a commitment is stated in. The finished goods' plan is one item per
 *  row in that item's own units — the grain the build-plan partition is already
 *  written at — so editing there needs no share of another item unpicked. Whole,
 *  never the repack sites' share of it: a plan is committed at every site. */
export const EDIT_PLAN_TYPE: BuildPlanType = "repack";

/** What the item column is CALLED while editing. Edit mode reads the repack
 *  plan, where the item IS the finished good, so the column a planner edits
 *  reads "Finished Goods SKU" rather than "Manufacturing SKU" over it. */
const REPACK_SEGMENT_LABEL: Record<string, string> = {
  production_sku: "Finished Goods SKU",
};

export function buildSegmentLabel(segment: string, planType: string): string {
  // The read-only readout beside the edited finished good — always the
  // manufacturing item, whatever plan is showing.
  if (segment === MANUFACTURING_READOUT_COL) return "Manufacturing SKU";
  if (segment === FACILITY_FUNCTION_COLUMN) return "Stage";
  if (planType === "repack" && segment in REPACK_SEGMENT_LABEL) {
    return REPACK_SEGMENT_LABEL[segment];
  }
  return BUILD_SEGMENT_LABEL[segment] ?? segment;
}

/** The item keyspace core carries on every row without being asked, so a cell
 *  can name the key PAIR a publish is keyed on. */
export const PRODUCTION_KEY_TYPE_COL = "production_item_key_type";

/** The item key itself, carried the same way while the grid shows its name. */
export const PRODUCTION_KEY_COL = "production_item_key";

/** The finished good a row ships as — on a Repack row, the pack it becomes. */
export const FINISHED_GOOD_KEY_COL = "finished_good_item_key";

/** The build facility (Manufacturing Facility). The In-Line / Repack lock scope
 *  keys on it, since the tab axis (facility_function) is a property of the site
 *  and a lock cell carries its facility. */
export const FACILITY_COLUMN = "facility";

/** The item's cases factor (eaches per case), carried on every row by core.
 *  A cell converts through its own row's factor, so this is read per row at the
 *  single-item lock grain. */
export const CASES_FACTOR_COL = "eaches_per_case";

/** The item's base-unit factor (cans / sticks per each), carried on every row by
 *  core like the cases factor. Edit mode shows and takes cans / sticks; a cell
 *  converts through its own row's factor, read per row at the lock grain. */
export const BASE_UNIT_FACTOR_COL = "base_unit_qty";

export const PLANNED_MEASURE = "planned_builds";
export const COMMITTED_MEASURE = "committed_builds";
export const RECOMMENDED_BUILD_MEASURE = "recommended_builds";
export const MATCHED_MEASURE = "matched_builds";
/** How much of a build is another item's demand restated into it — not offered
 *  as a view, read per row to know which items cannot be committed as they
 *  stand. */
export const REMAPPED_MEASURE = "remapped_builds";

export const BUILD_MEASURE_ORDER: string[] = [
  PLANNED_MEASURE,
  COMMITTED_MEASURE,
  RECOMMENDED_BUILD_MEASURE,
  MATCHED_MEASURE,
];

export const BUILD_MEASURE_LABEL: Record<string, string> = {
  planned_builds: "Planned Builds",
  committed_builds: "Committed",
  recommended_builds: "Recommended",
  matched_builds: "Matched",
};

/** What a planner commits: the whole build for the period, which is what the
 *  grid shows and therefore what a lock states. */
export const EDIT_MEASURE = PLANNED_MEASURE;

/** The unit the model and the publish stay in: a version is stamped in eaches.
 *  The edit grid DISPLAYS cans / sticks, converting each cell through its own
 *  row's base-unit factor at the single-item lock grain — so the model, the
 *  overlay and the publish never leave eaches. See EDIT_DISPLAY_UNITS. */
export const EDIT_UNITS: BuildUnits = "eaches";

/** What the edit grid SHOWS: cans / sticks. The conversion is per row (a cell's
 *  own eaches × its base-unit factor); an item that resolves no factor shows a
 *  dash and cannot be committed, which the publisher already refuses. */
export const EDIT_DISPLAY_UNITS: BuildUnits = BUILD_BASE_UNIT_MODE;

/** The grain a week column is locked at. Month is a display rollup of it. */
export const EDIT_PERIOD = "week";

/** The three columns the lock is read off, named so the glyph and the grid
 *  cannot disagree about which is which. */
export const BUILD_ARRIVALS_MEASURE = PLANNED_MEASURE;
export const BUILD_FIRM_MEASURE = COMMITTED_MEASURE;
export const BUILD_RECOMMENDED_MEASURE = RECOMMENDED_BUILD_MEASURE;

/** A cell's printed value, whole in BOTH units: the plan's own numbers are
 *  fractional, and rounding one unit but not the other reads as a broken
 *  conversion. A null is a blank, never a zero. The kit's quantity format
 *  (whole, grouped, negative zero as 0) — a build is a quantity column. */
export function formatBuildCell(value: number | null | undefined): string {
  return formatMeasureCell(BUILD_PLAN_MEASURE, value == null ? Number.NaN : value);
}

// ── The Production pivot's view params ──────────────────────────────────────
// Here rather than in the cube so the page's engine declaration (enginePages.ts)
// reads the same defaults and choices the pivot does, without loading the pivot.

// Changing this re-reads, because core does the bucketing — the cells are a
// calendar bucket, not weeks added up here.
export const BUILD_PERIOD_OPTIONS: { value: BuildPeriod; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

// Its OWN param names: both tabs share one query string, and the frozen-location
// machinery isolates reads but not writes.
export const BUILD_VIEW_PARAMS = {
  buildSegments: DEFAULT_BUILD_SEGMENTS,
  buildMeasure: PLANNED_MEASURE as string,
  buildUnits: "cases" as BuildUnits,
  // Month, because the committed plan is uploaded monthly and the ingest
  // divides it across the month's days — a week column is that division
  // re-summed, which is a number nobody planned.
  buildPeriod: "month" as BuildPeriod,
  // Read only by a mount that offers the stage (the Build Plan tab).
  buildStage: DEFAULT_BUILD_STAGE as BuildStage,
  // The co-man's plan: what the page has always shown, and the arm the Build
  // Plan (Engine) report is crossed against cell for cell.
  buildPlanType: DEFAULT_BUILD_PLAN_TYPE as BuildPlanType,
  // No default. The view shows every class the run built; narrowing is the
  // planner's, and a literal here would be one client's taxonomy in the code.
  buildFilters: [] as FilterEntry[],
};

/** The units a mount offers, its base unit named for its product line where the view names it. */
export const buildUnitOptionsFor = (view?: BuildPlanView): { value: BuildUnits; label: string }[] =>
  view?.baseUnitLabel
    ? BUILD_UNIT_OPTIONS.map((o) => (o.value === BUILD_BASE_UNIT_MODE ? { ...o, label: view.baseUnitLabel! } : o))
    : BUILD_UNIT_OPTIONS;

/** The lower-case unit word captions and notices use, by the same rule. */
export const buildUnitLabelFor = (units: BuildUnits, view?: BuildPlanView): string =>
  units === BUILD_BASE_UNIT_MODE && view?.baseUnitLabel ? view.baseUnitLabel.toLowerCase() : BUILD_UNIT_LABEL[units];

/** The period grains a mount offers: its view's, or every calendar grain. */
export const buildPeriodOptionsFor = (view?: BuildPlanView): { value: BuildPeriod; label: string }[] =>
  view ? view.periods.map((value) => ({ value, label: BUILD_PERIOD_LABEL[value] })) : BUILD_PERIOD_OPTIONS;

/** Every Build Plan tab plans builds; the halves stay on the Production pivot. */
export const buildMeasureOrderFor = (view?: BuildPlanView): string[] =>
  view ? [PLANNED_MEASURE] : BUILD_MEASURE_ORDER;

/** A mount's defaults: a view opens on its own grouping and its first grain. */
export const buildViewParamsFor = (view?: BuildPlanView): typeof BUILD_VIEW_PARAMS =>
  view
    ? { ...BUILD_VIEW_PARAMS, buildSegments: [...view.defaultSegments], buildPeriod: view.periods[0] }
    : BUILD_VIEW_PARAMS;
